/* eslint-disable @typescript-eslint/naming-convention */

import { Observable, catchError, map, of, tap } from 'rxjs';
import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';

import { AuthService } from 'app/core/auth/auth.service';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { apiErrorMessage } from 'app/services/api-error.helpers';
import { environment } from 'environments/environment';

interface OAuthTokenResponse {
    access_token: string;
    expires_in: number;
}

@Injectable()
export class QueriesService {
    private readonly _apiBaseUrl = environment.apiBaseUrl;

    constructor(
        private _httpClient: HttpClient,
        private _router: Router,
        private _snackBar: MatSnackBar,
        private _authService: AuthService,
    ) {
    }

    cCredentials(): Observable<boolean> {
        const now = Date.now();
        const expiresAt = Number(localStorage.getItem('client_tkExpires') ?? 0);
        const token = localStorage.getItem('client_accessToken') ?? '';

        if (token && expiresAt && now < expiresAt) {
            return of(true);
        }

        const body = {
            client_id: environment.oauthClientId,
            grant_type: 'client_credentials',
            client_secret: environment.oauthClientSecret,
        };

        return this._httpClient.post<OAuthTokenResponse>(this._apiBaseUrl + 'oauth/token', body).pipe(
            tap((response) => this._storeClientToken(response)),
            map(() => true),
            catchError(() => of(false)),
        );
    }

    clientCredentials(): Observable<any> {
        const body = {
            client_id: environment.oauthClientId,
            grant_type: 'client_credentials',
            client_secret: environment.oauthClientSecret,
        };

        return this._httpClient.post(this._apiBaseUrl + 'oauth/token', body);
    }

    manageErrors(error): any {
        if (error.status === 401) {
            this._router.navigate(['/sign-out']);
        } else if (error.status === 500) {
            this._snackBar.open(
                'Ha ocurrido un error inesperado: ' + error.error.message,
                'OK',
                {
                    duration: 10000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar'],
                }
            );
        } else if (error.status === 404) {
            if (error.error.error) {
                this._snackBar.open(
                    error.error.error,
                    'OK',
                    {
                        duration: 3000,
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                        panelClass: ['error-snackbar'],
                    }
                );
            } else {
                this._router.navigate(['/404']);
            }
        } else if (error.status === 403) {
            this._router.navigate(['/403']);
        } else if (error.status === 409) {
            this._snackBar.open(
                error.error.error,
                'OK',
                {
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar'],
                }
            );
            return error.error.error;
        } else if (error.status === 422) {
            // Los 422 de validación traen un objeto con los mensajes de cada
            // campo, no un texto: apiErrorMessage() los aplana.
            const message = apiErrorMessage(error);

            this._snackBar.open(
                message,
                'OK',
                {
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar'],
                }
            );
            return message;
        } else if (error.error?.errors?.identification) {
            this._snackBar.open(
                'Ya existe un usuario con la misma identificación',
                'OK',
                {
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar'],
                }
            );
            return 'Ya existe un usuario con la misma identificación';
        } else {
            this._snackBar.open(
                'Ha ocurrido un error inesperado',
                'OK',
                {
                    duration: 3000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar'],
                }
            );
            return 'Ha ocurrido un error inesperado';
        }
    }

    getDatos(endpoint, responseType: 'json' | 'text' | 'blob' = 'json'): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });

        return this._httpClient.get(this._apiBaseUrl + endpoint, { headers, responseType: responseType as 'json' });
    }

    /**
     * GET con la respuesta completa, para leer sus cabeceras (`Retry-After`
     * del polling de la importación documental).
     */
    getResponse<T = any>(endpoint: string): Observable<HttpResponse<T>> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });

        return this._httpClient.get<T>(this._apiBaseUrl + endpoint, { headers, observe: 'response' });
    }

    /**
     * POST de un fichero con el progreso de la subida: emite los eventos de
     * HttpClient (`UploadProgress` y, al final, la `HttpResponse` con su
     * código, que distingue un 202 de un 200).
     */
    upload<T = any>(datos: FormData, ruta: string): Observable<HttpEvent<T>> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });

        return this._httpClient.post<T>(this._apiBaseUrl + ruta, datos, { headers, reportProgress: true, observe: 'events' });
    }

    getDatosCC(endpoint): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + localStorage.getItem('client_accessToken'),
        });
        return this._httpClient.get(this._apiBaseUrl + endpoint, { headers });
    }

    updateModelCC(datos, ruta): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + localStorage.getItem('client_accessToken'),
        });
        return this._httpClient.put(this._apiBaseUrl + ruta, datos, { headers });
    }

    createModelCC(datos, ruta): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + localStorage.getItem('client_accessToken'),
        });
        return this._httpClient.post(this._apiBaseUrl + ruta, datos, { headers });
    }

    deleteModelCC(endpoint): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + localStorage.getItem('client_accessToken'),
        });

        return this._httpClient.delete(this._apiBaseUrl + endpoint, { headers });
    }

    /**
     * POST que devuelve el cuerpo de la respuesta. Para subir un fichero con su
     * progreso, upload().
     */
    createModel(datos, ruta): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });

        return this._httpClient.post(this._apiBaseUrl + ruta, datos, { headers });
    }

    updateModel(datos, ruta): Observable<any> {
        const cabeceras = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });

        return this._httpClient.put(this._apiBaseUrl + ruta, datos, { headers: cabeceras });
    }

    deleteModel(endpoint): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });

        return this._httpClient.delete(this._apiBaseUrl + endpoint, { headers });
    }

    patchModel(datos, ruta): Observable<any> {
        const cabeceras = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });

        return this._httpClient.patch(this._apiBaseUrl + ruta, datos, { headers: cabeceras });
    }

    private _storeClientToken(response: OAuthTokenResponse): void {
        const now = new Date();
        const expiresAt = now.setSeconds(response.expires_in);

        localStorage.setItem('client_accessToken', response.access_token ?? '');
        localStorage.setItem('client_tkExpires', expiresAt.toString());
    }
}
